import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  GitBranch, Plus, ChevronRight, ChevronDown, Building2, Users, Calendar,
  FileText, DollarSign, Loader2, RefreshCw, Sparkles, Pencil, MapPin, KeyRound, Wand2, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const jheaders = () => ({ 'Content-Type': 'application/json', ...headers() });

const ENTITY_TYPES = [
  'Trust / Holding', 'University (UGC)', 'Engineering (AICTE)', 'Polytechnic',
  'School', 'Junior College', 'Coaching', 'Branch', 'Sub-branch', 'Other'
];

// Iteratively flatten a tree into a list of {node, depth, hasChildren, parentId}
function flattenTree(roots) {
  const out = [];
  const stack = [];
  const rootsCopy = [...(roots || [])];
  for (let i = rootsCopy.length - 1; i >= 0; i--) {
    stack.push({ node: rootsCopy[i], depth: 0 });
  }
  while (stack.length) {
    const { node, depth } = stack.pop();
    const children = node.children || [];
    out.push({
      entity_id: node.entity_id,
      name: node.name,
      type: node.type,
      city: node.city,
      state: node.state,
      parent_entity_id: node.parent_entity_id,
      depth,
      hasChildren: children.length > 0,
      raw: node,
    });
    for (let i = children.length - 1; i >= 0; i--) {
      stack.push({ node: children[i], depth: depth + 1 });
    }
  }
  return out;
}

// Compute descendant id set for collapse/expand operations
function descendantsOf(flat, entityId) {
  const idx = flat.findIndex((n) => n.entity_id === entityId);
  if (idx === -1) return new Set();
  const baseDepth = flat[idx].depth;
  const ids = new Set();
  for (let i = idx + 1; i < flat.length; i++) {
    if (flat[i].depth <= baseDepth) break;
    ids.add(flat[i].entity_id);
  }
  return ids;
}

export default function Branches() {
  const { user } = useAuth();
  const canEdit = ['super_admin', 'admin', 'branch_admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const isInstituteAdmin = user?.role === 'branch_admin';

  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState({ roots: [] });
  const [dashboard, setDashboard] = useState({ kpis: {}, breakdown: [], scope_count: 0 });
  const [collapsed, setCollapsed] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [seedOpen, setSeedOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardForm, setWizardForm] = useState({
    institute: { name: '', type: 'Engineering (AICTE)', code: '', city: '', state: 'Maharashtra', contact_email: '', contact_phone: '', address: '', parent_entity_id: '', enabled_modules: [] },
    branches: [{ name: '', code: '', type: 'Branch', city: '', state: '' }],
    admin: { name: '', email: '', phone: '', password: '' }
  });
  const [wizardResult, setWizardResult] = useState(null);
  const [form, setForm] = useState({
    name: '', type: 'Branch', code: '', city: '', state: '',
    contact_email: '', contact_phone: '', address: '', parent_entity_id: ''
  });
  const [seedForm, setSeedForm] = useState({
    name: 'Sou. Sushila Dhanchand Ghodawat Charitable Trust', code: 'SSDGCT', city: 'Kolhapur', state: 'Maharashtra'
  });
  const [submitting, setSubmitting] = useState(false);

  // List of toggle-able modules at institute creation time (matches sidebar items + functional modules)
  const INSTITUTE_MODULES = [
    { key: 'attendance', label: 'Attendance' },
    { key: 'leaves', label: 'Leaves' },
    { key: 'payroll', label: 'Payroll' },
    { key: 'recruitment', label: 'Recruitment' },
    { key: 'performance', label: 'Performance' },
    { key: 'lnd', label: 'Learning & Development' },
    { key: 'documents', label: 'Documents' },
    { key: 'notices', label: 'Notices' },
    { key: 'reports', label: 'Reports & Analytics' },
    { key: 'ai_report', label: 'AI Report' },
    { key: 'audit', label: 'Audit Trail' },
  ];
  const toggleModule = (key) => setWizardForm((f) => {
    const cur = new Set(f.institute.enabled_modules || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    return { ...f, institute: { ...f.institute, enabled_modules: Array.from(cur) } };
  });
  const allModulesOn = (wizardForm.institute.enabled_modules || []).length === INSTITUTE_MODULES.length;
  const toggleAllModules = () => setWizardForm((f) => ({
    ...f,
    institute: { ...f.institute, enabled_modules: allModulesOn ? [] : INSTITUTE_MODULES.map((m) => m.key) },
  }));

  const fetchTree = useCallback(async () => {
    try {
      const res = await fetch(API + '/api/branches/tree', { headers: headers() });
      const data = await res.json();
      setTree(data || { roots: [] });
    } catch (e) {
      toast.error('Failed to load entity tree');
    }
  }, []);

  const fetchDashboard = useCallback(async (entityId) => {
    try {
      const qs = entityId ? '?entity_id=' + encodeURIComponent(entityId) : '';
      const res = await fetch(API + '/api/branches/consolidated-dashboard' + qs, { headers: headers() });
      const data = await res.json();
      setDashboard(data || { kpis: {}, breakdown: [], scope_count: 0 });
    } catch (e) {
      toast.error('Failed to load consolidated dashboard');
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchTree(), fetchDashboard(selected?.entity_id)]);
    setLoading(false);
  }, [fetchTree, fetchDashboard, selected]);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const handleSelect = (node) => {
    setSelected(node);
    fetchDashboard(node.entity_id);
  };

  const toggleNode = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAddChild = (parent) => {
    setEditing(null);
    setForm({
      name: '', type: 'Branch', code: '',
      city: parent.city || '', state: parent.state || '',
      contact_email: '', contact_phone: '', address: '',
      parent_entity_id: parent.entity_id
    });
    setShowForm(true);
  };

  const handleAddRoot = () => {
    setEditing(null);
    // Branch admin (Institute Admin) creates branches under their OWN institute, not at root
    if (isInstituteAdmin && user?.entity_id) {
      setForm({ name: '', type: 'Branch', code: '', city: '', state: '', contact_email: '', contact_phone: '', address: '', parent_entity_id: user.entity_id, independent_unit: false });
    } else {
      setForm({ name: '', type: 'Trust / Holding', code: '', city: '', state: '', contact_email: '', contact_phone: '', address: '', parent_entity_id: '', independent_unit: false });
    }
    setShowForm(true);
  };

  const handleEdit = (node) => {
    setEditing(node);
    setForm({
      name: node.name || '',
      type: node.type || 'Branch',
      code: node.code || '',
      city: node.city || '',
      state: node.state || '',
      contact_email: node.contact_email || '',
      contact_phone: node.contact_phone || '',
      address: node.address || '',
      parent_entity_id: node.parent_entity_id || '',
      independent_unit: node.independent_unit === true
    });
    setShowForm(true);
  };

  // Delete entity — first tries safe delete; if backend returns 400 because children
  // exist, prompts user to confirm a FORCE cascade delete.
  const handleDelete = async (node) => {
    if (!isSuperAdmin) return;
    if (!window.confirm(`Delete "${node.name}"?\n\nIf it has children/users/employees you'll be asked to confirm a cascade delete.`)) return;
    try {
      let res = await fetch(`${API}/api/entities/${node.entity_id}`, { method: 'DELETE', headers: headers(), credentials: 'include' });
      let data = await safeJson(res);
      if (res.status === 400 && data.detail) {
        const cascadeOk = window.confirm(
          `${data.detail}\n\nDo you want to FORCE-DELETE everything underneath?\nThis cannot be undone.`
        );
        if (!cascadeOk) return;
        res = await fetch(`${API}/api/entities/${node.entity_id}?force=true`, {
          method: 'DELETE', headers: headers(), credentials: 'include',
        });
        data = await safeJson(res);
      }
      if (!res.ok) throw new Error(data.detail || 'Delete failed');
      const msg = data.force
        ? `Force-deleted ${data.deleted_entities} entit(y/ies), ${data.deleted_users} users, ${data.deleted_employees} employees`
        : (data.message || 'Entity deleted');
      toast.success(msg);
      if (selected?.entity_id === node.entity_id) setSelected(null);
      fetchTree();
    } catch (e) { toast.error(e.message); }
  };

  const submitForm = async () => {
    const name = (form.name || '').trim();
    const code = (form.code || '').trim();
    const type = (form.type || '').trim();
    if (!name || !code || !type) {
      toast.error('Name, type and code are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.parent_entity_id) payload.parent_entity_id = null;
      const url = editing
        ? API + '/api/entities/' + editing.entity_id
        : API + '/api/entities';
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: jheaders(), body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success(editing ? 'Branch updated' : 'Branch created');
      setShowForm(false);
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const seedTrustRoot = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(API + '/api/branches/seed-trust-root', {
        method: 'POST', headers: jheaders(), body: JSON.stringify(seedForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Seed failed');
      toast.success(data.message || 'Trust root created');
      setSeedOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Seed failed');
    } finally {
      setSubmitting(false);
    }
  };

  const rebuildAncestors = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(API + '/api/branches/rebuild-ancestors', { method: 'POST', headers: jheaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Rebuild failed');
      toast.success('Rebuilt ' + data.updated + ' entity rows');
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Rebuild failed');
    } finally {
      setSubmitting(false);
    }
  };

  const inheritSettings = async (node) => {
    if (!node?.entity_id) return;
    const ok = window.confirm(
      `Inherit Leave Policies, Approval Hierarchies and Geo-Fence config for "${node.name}" from its parent?\n\n` +
      `Existing settings on this branch will be kept — only missing ones will be copied.`
    );
    if (!ok) return;
    try {
      const res = await fetch(API + '/api/branches/' + node.entity_id + '/inherit-settings', {
        method: 'POST', headers: jheaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Inherit failed');
      if (!data.source_entity_id) {
        toast.message('No ancestor has settings to inherit yet.');
        return;
      }
      const bits = [];
      if (data.leave_policies) bits.push(data.leave_policies + ' leave polic' + (data.leave_policies === 1 ? 'y' : 'ies'));
      if (data.approval_hierarchies) bits.push(data.approval_hierarchies + ' approval hierarch' + (data.approval_hierarchies === 1 ? 'y' : 'ies'));
      if (data.geofence_config) bits.push('geo-fence config');
      toast.success(bits.length ? 'Inherited ' + bits.join(', ') : 'Already in sync — nothing to copy.');
    } catch (e) {
      toast.error(e.message || 'Inherit failed');
    }
  };

  // ---- Reset Institute Admin Password ----
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const openResetAdmin = (node) => { setResetTarget(node); setResetResult(null); };
  const doResetAdmin = async () => {
    if (!resetTarget) return;
    setSubmitting(true);
    try {
      const res = await fetch(API + '/api/branches/reset-institute-admin-password', {
        method: 'POST', headers: jheaders(),
        body: JSON.stringify({ entity_id: resetTarget.entity_id })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      setResetResult(data);
      toast.success('Password reset. Share these credentials with the admin.');
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Institute Wizard ----
  const openWizard = () => {
    // Pre-select Trust root if one exists (level 0, type contains "Trust")
    const trustRoot = (tree.roots || []).find((r) => (r.level || 0) === 0 && /trust/i.test(r.type || ''));
    setWizardForm({
      institute: { name: '', type: 'Engineering (AICTE)', code: '', city: '', state: 'Maharashtra', contact_email: '', contact_phone: '', address: '', parent_entity_id: trustRoot?.entity_id || '' },
      branches: [{ name: '', code: '', type: 'Branch', city: '', state: '' }],
      admin: { name: '', email: '', phone: '', password: '' }
    });
    setWizardStep(1);
    setWizardResult(null);
    setWizardOpen(true);
  };

  const addBranchRow = () => {
    setWizardForm((f) => ({ ...f, branches: [...f.branches, { name: '', code: '', type: 'Branch', city: f.institute.city || '', state: f.institute.state || '' }] }));
  };
  const removeBranchRow = (idx) => {
    setWizardForm((f) => ({ ...f, branches: f.branches.filter((_, i) => i !== idx) }));
  };
  const updateBranch = (idx, field, value) => {
    setWizardForm((f) => ({
      ...f,
      branches: f.branches.map((b, i) => (i === idx ? { ...b, [field]: value } : b))
    }));
  };

  const validateStep1 = () => {
    const i = wizardForm.institute;
    if (!i.name?.trim()) return 'Institute name is required';
    if (!i.code?.trim()) return 'Institute code is required';
    if (!i.type?.trim()) return 'Institute type is required';
    return null;
  };

  const validateStep3 = () => {
    const a = wizardForm.admin;
    if (!a.name?.trim()) return 'Admin name is required';
    if (!a.email?.trim()) return 'Admin email is required';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a.email)) return 'Admin email is not valid';
    return null;
  };

  const submitWizard = async () => {
    const err = validateStep3();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        institute: { ...wizardForm.institute, parent_entity_id: wizardForm.institute.parent_entity_id || null },
        branches: wizardForm.branches.filter((b) => (b.name || '').trim() && (b.code || '').trim()),
        admin: { ...wizardForm.admin, password: wizardForm.admin.password || undefined }
      };
      const res = await fetch(API + '/api/branches/institute-with-branches', {
        method: 'POST', headers: jheaders(), body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Creation failed');
      setWizardResult(data);
      toast.success('Institute, branches, and admin created');
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Creation failed');
    } finally {
      setSubmitting(false);
    }
  };
  // ---- End wizard ----


  const flat = useMemo(() => flattenTree(tree.roots || []), [tree]);

  // Hide rows whose ancestor is collapsed
  const visibleRows = useMemo(() => {
    if (collapsed.size === 0) return flat;
    const hidden = new Set();
    for (const id of collapsed) {
      const desc = descendantsOf(flat, id);
      desc.forEach((d) => hidden.add(d));
    }
    return flat.filter((r) => !hidden.has(r.entity_id));
  }, [flat, collapsed]);

  const fmtINR = (n) => '\u20B9' + (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <GitBranch className="h-7 w-7 text-blue-600" /> Branches &amp; Multi-City Roll-up
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Hierarchical view of every institute / branch / sub-branch. Select a node to roll up KPIs across all its descendants.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={refresh} disabled={loading} data-testid="branches-refresh-btn">
            <RefreshCw className={'h-4 w-4 mr-2 ' + (loading ? 'animate-spin' : '')} /> Refresh
          </Button>
          {canEdit ? (
            <Button onClick={handleAddRoot} variant="outline" data-testid="branches-add-root-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Branch
            </Button>
          ) : null}
          {isSuperAdmin ? (
            <Button onClick={openWizard} data-testid="branches-create-institute-btn">
              <Building2 className="h-4 w-4 mr-2" /> Create Institute
            </Button>
          ) : null}
          {isSuperAdmin ? (
            <Button variant="secondary" onClick={() => setSeedOpen(true)} data-testid="branches-seed-btn">
              <Sparkles className="h-4 w-4 mr-2" /> Seed Trust Root
            </Button>
          ) : null}
          {isSuperAdmin ? (
            <Button variant="ghost" onClick={rebuildAncestors} disabled={submitting} data-testid="branches-rebuild-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Rebuild Ancestors
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-employees">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Users className="h-4 w-4" /> Total Employees
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{dashboard.kpis?.total_employees ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1">
              across {dashboard.scope_count || 0} {dashboard.scope_count === 1 ? 'entity' : 'entities'}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-present">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Present Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{dashboard.kpis?.present_today ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1">{dashboard.kpis?.attendance_percent ?? 0}% attendance</div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-pending-leaves">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <FileText className="h-4 w-4" /> Pending Leaves
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{dashboard.kpis?.pending_leaves ?? 0}</div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-payroll">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> Monthly Payroll
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{fmtINR(dashboard.kpis?.monthly_payroll || 0)}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Entity Tree</span>
              {selected ? (
                <Button size="sm" variant="ghost" onClick={() => { setSelected(null); fetchDashboard(); }}
                        data-testid="branches-clear-selection">
                  Clear
                </Button>
              ) : null}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
              </div>
            ) : visibleRows.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">
                No entities yet. Click "Add Branch" to create one.
              </div>
            ) : (
              <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                {visibleRows.map((row) => {
                  const isCollapsed = collapsed.has(row.entity_id);
                  const isSelected = selected?.entity_id === row.entity_id;
                  return (
                    <div
                      key={row.entity_id}
                      data-testid={'branch-node-' + row.entity_id}
                      className={'group flex items-center gap-2 py-2 px-2 rounded-md transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer ' + (isSelected ? 'bg-blue-50 dark:bg-blue-950 ring-1 ring-blue-300' : '')}
                      style={{ paddingLeft: (row.depth * 20 + 8) + 'px' }}
                      onClick={() => handleSelect(row.raw)}
                    >
                      {row.hasChildren ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleNode(row.entity_id); }}
                          className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
                          data-testid={'branch-toggle-' + row.entity_id}
                        >
                          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      ) : (
                        <span className="w-5 inline-block" />
                      )}
                      <Building2 className="h-4 w-4 text-slate-500" />
                      <span className="font-medium text-sm">{row.name}</span>
                      <Badge variant="outline" className="text-xs">{row.type}</Badge>
                      {row.city ? (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />{row.city}{row.state ? (', ' + row.state) : ''}
                        </span>
                      ) : null}
                      <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {canEdit ? (
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                                  onClick={(e) => { e.stopPropagation(); handleAddChild(row.raw); }}
                                  data-testid={'branch-add-child-' + row.entity_id}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        ) : null}
                        {canEdit ? (
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                                  onClick={(e) => { e.stopPropagation(); handleEdit(row.raw); }}
                                  data-testid={'branch-edit-' + row.entity_id}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                        ) : null}
                        {isSuperAdmin ? (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-600"
                                  title="Reset Institute Admin Password"
                                  onClick={(e) => { e.stopPropagation(); openResetAdmin(row.raw); }}
                                  data-testid={'branch-reset-admin-' + row.entity_id}>
                            <KeyRound className="h-3 w-3" />
                          </Button>
                        ) : null}
                        {canEdit && row.raw.parent_entity_id ? (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-blue-600"
                                  title="Inherit settings (leave policies, approval hierarchies, geo-fence) from parent"
                                  onClick={(e) => { e.stopPropagation(); inheritSettings(row.raw); }}
                                  data-testid={'branch-inherit-' + row.entity_id}>
                            <Wand2 className="h-3 w-3" />
                          </Button>
                        ) : null}
                        {isSuperAdmin ? (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50"
                                  title="Delete entity (force cascade for non-empty)"
                                  onClick={(e) => { e.stopPropagation(); handleDelete(row.raw); }}
                                  data-testid={'branch-delete-' + row.entity_id}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        ) : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {selected ? ('Roll-up under: ' + selected.name) : 'All Branches (Root Roll-up)'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table data-testid="branches-breakdown-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Branch</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead className="text-right">Staff</TableHead>
                    <TableHead className="text-right">Present</TableHead>
                    <TableHead className="text-right">Att %</TableHead>
                    <TableHead className="text-right">Pending Leaves</TableHead>
                    <TableHead className="text-right">Payroll</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(dashboard.breakdown || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 py-6">No data</TableCell>
                    </TableRow>
                  ) : (
                    dashboard.breakdown.map((b) => (
                      <TableRow key={b.entity_id} data-testid={'breakdown-row-' + b.entity_id}>
                        <TableCell className="font-medium">
                          <span style={{ paddingLeft: ((b.level || 0) * 8) + 'px' }} className="inline-block">
                            {b.name}
                          </span>
                          <div className="text-xs text-slate-500">{b.type}</div>
                        </TableCell>
                        <TableCell className="text-sm">{b.city || '-'}{b.state ? (', ' + b.state) : ''}</TableCell>
                        <TableCell className="text-right">{b.employees}</TableCell>
                        <TableCell className="text-right">{b.present_today}</TableCell>
                        <TableCell className="text-right">{b.attendance_percent}%</TableCell>
                        <TableCell className="text-right">{b.pending_leaves}</TableCell>
                        <TableCell className="text-right">{fmtINR(b.monthly_payroll)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="branches-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Branch' : 'Add Branch'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update branch details. Moving a parent will cascade ancestors automatically.'
                : 'Create a new branch under a parent (or leave parent empty for a root).'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Parent</Label>
              <Select
                value={form.parent_entity_id || 'NONE'}
                onValueChange={(v) => setForm((f) => ({ ...f, parent_entity_id: v === 'NONE' ? '' : v }))}
              >
                <SelectTrigger data-testid="branches-form-parent"><SelectValue placeholder="(No parent - root)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">(No parent - root entity)</SelectItem>
                  {flat
                    .filter((e) => !editing || e.entity_id !== editing.entity_id)
                    .map((e) => (
                      <SelectItem key={e.entity_id} value={e.entity_id}>
                        {'\u00A0\u00A0'.repeat(e.depth)}{e.name} ({e.type})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="branches-form-name" />
            </div>
            <div>
              <Label>Code *</Label>
              <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} data-testid="branches-form-code" />
            </div>
            <div>
              <Label>Type *</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="branches-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} data-testid="branches-form-city" />
            </div>
            <div>
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} data-testid="branches-form-state" />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} data-testid="branches-form-email" />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} data-testid="branches-form-phone" />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="branches-form-address" />
            </div>
            <div className="col-span-2 flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <div className="pr-2">
                <Label className="text-sm">Separate / independent institute</Label>
                <p className="text-[11px] leading-snug text-slate-500 mt-0.5">
                  Keep this unit's staff out of its parent's attendance &amp; payroll roll-ups
                  (e.g. SG&nbsp;ITI under the trust). Its staff show only when this institute is selected directly.
                </p>
              </div>
              <Switch
                checked={form.independent_unit === true}
                onCheckedChange={(v) => setForm((f) => ({ ...f, independent_unit: v }))}
                data-testid="branches-form-independent"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={submitForm} disabled={submitting} data-testid="branches-form-submit">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editing ? 'Save Changes' : 'Create Branch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Institute Wizard */}
      <Dialog open={wizardOpen} onOpenChange={(v) => { if (!submitting) setWizardOpen(v); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="branches-wizard-dialog">
          <DialogHeader>
            <DialogTitle>
              {wizardResult ? 'Institute Created' : 'Create New Institute'}
              {!wizardResult ? <span className="text-sm font-normal text-slate-500 ml-2">— Step {wizardStep} of 3</span> : null}
            </DialogTitle>
            <DialogDescription>
              {wizardResult
                ? 'Save these admin credentials — they are shown only once.'
                : wizardStep === 1
                  ? 'Institute / college level details. The institute will be placed under your Trust root automatically.'
                  : wizardStep === 2
                    ? 'Add branches that belong to this institute. Each row creates a sub-branch. Leave empty to add later.'
                    : 'This admin will get full management access to ONLY this institute and its branches.'}
            </DialogDescription>
          </DialogHeader>

          {wizardResult ? (
            <div className="space-y-3" data-testid="wizard-result">
              <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-900 p-4">
                <div className="font-semibold text-green-900 dark:text-green-100">Institute Admin Login</div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div className="text-slate-500">Email</div><div className="font-mono" data-testid="wizard-result-email">{wizardResult.admin?.email}</div>
                  <div className="text-slate-500">Password</div><div className="font-mono select-all" data-testid="wizard-result-password">{wizardResult.admin?.temp_password}</div>
                  <div className="text-slate-500">Role</div><div>{wizardResult.admin?.role}</div>
                  <div className="text-slate-500">Scope</div><div>{wizardResult.institute?.name}</div>
                </div>
                <Button size="sm" variant="outline" className="mt-3" onClick={() => {
                  const text = 'Login URL: ' + window.location.origin + '/login\nEmail: ' + wizardResult.admin?.email + '\nPassword: ' + wizardResult.admin?.temp_password;
                  navigator.clipboard?.writeText(text);
                  toast.success('Credentials copied to clipboard');
                }} data-testid="wizard-copy-creds-btn">
                  Copy credentials
                </Button>
              </div>
              <div className="text-sm">
                <div className="font-medium">Institute: {wizardResult.institute?.name}</div>
                <div className="text-slate-500">Branches: {wizardResult.branches?.length || 0}</div>
                {(wizardResult.branches || []).map((b) => (
                  <div key={b.entity_id} className="text-slate-600 dark:text-slate-400 text-xs">• {b.name} ({b.code})</div>
                ))}
              </div>
              <DialogFooter>
                <Button onClick={() => setWizardOpen(false)} data-testid="wizard-close-btn">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              {wizardStep === 1 ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Parent (Trust / Holding)</Label>
                    <Select value={wizardForm.institute.parent_entity_id || 'NONE'}
                            onValueChange={(v) => setWizardForm((f) => ({ ...f, institute: { ...f.institute, parent_entity_id: v === 'NONE' ? '' : v } }))}>
                      <SelectTrigger data-testid="wizard-institute-parent"><SelectValue placeholder="(No parent - root)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">(No parent - root)</SelectItem>
                        {flat.map((e) => (
                          <SelectItem key={e.entity_id} value={e.entity_id}>
                            {'\u00A0\u00A0'.repeat(e.depth)}{e.name} ({e.type})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Institute Name *</Label>
                    <Input value={wizardForm.institute.name} onChange={(e) => setWizardForm((f) => ({ ...f, institute: { ...f.institute, name: e.target.value } }))} data-testid="wizard-institute-name" />
                  </div>
                  <div>
                    <Label>Code *</Label>
                    <Input value={wizardForm.institute.code} onChange={(e) => setWizardForm((f) => ({ ...f, institute: { ...f.institute, code: e.target.value.toUpperCase() } }))} data-testid="wizard-institute-code" />
                  </div>
                  <div>
                    <Label>Type *</Label>
                    <Select value={wizardForm.institute.type}
                            onValueChange={(v) => setWizardForm((f) => ({ ...f, institute: { ...f.institute, type: v } }))}>
                      <SelectTrigger data-testid="wizard-institute-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input value={wizardForm.institute.city} onChange={(e) => setWizardForm((f) => ({ ...f, institute: { ...f.institute, city: e.target.value } }))} data-testid="wizard-institute-city" />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input value={wizardForm.institute.state} onChange={(e) => setWizardForm((f) => ({ ...f, institute: { ...f.institute, state: e.target.value } }))} data-testid="wizard-institute-state" />
                  </div>
                  <div>
                    <Label>Contact Email</Label>
                    <Input value={wizardForm.institute.contact_email} onChange={(e) => setWizardForm((f) => ({ ...f, institute: { ...f.institute, contact_email: e.target.value } }))} data-testid="wizard-institute-email" />
                  </div>
                  <div className="col-span-2">
                    <Label>Address</Label>
                    <Input value={wizardForm.institute.address} onChange={(e) => setWizardForm((f) => ({ ...f, institute: { ...f.institute, address: e.target.value } }))} data-testid="wizard-institute-address" />
                  </div>
                  <div className="col-span-2 border-t pt-3 mt-1">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <Label className="text-sm font-semibold">Modules to enable</Label>
                        <p className="text-xs text-slate-500">Only ticked modules will appear in the sidebar for users of this institute and its branches. Tick all if unsure — you can change this later.</p>
                      </div>
                      <Button size="sm" variant="outline" type="button" onClick={toggleAllModules} data-testid="wizard-toggle-all-modules">
                        {allModulesOn ? 'Uncheck all' : 'Tick all'}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {INSTITUTE_MODULES.map((m) => {
                        const on = (wizardForm.institute.enabled_modules || []).includes(m.key);
                        return (
                          <label key={m.key} className={'flex items-center gap-2 rounded-md border p-2 cursor-pointer text-sm ' + (on ? 'bg-blue-50 dark:bg-blue-950 border-blue-300' : 'border-slate-200 dark:border-slate-800')} data-testid={'wizard-module-' + m.key}>
                            <Checkbox checked={on} onCheckedChange={() => toggleModule(m.key)} />
                            <span>{m.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {wizardStep === 2 ? (
                <div className="space-y-3">
                  <div className="text-xs text-slate-500">
                    Tip: you can skip this step entirely and add branches later from the tree view.
                  </div>
                  {wizardForm.branches.map((b, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2" data-testid={'wizard-branch-row-' + idx}>
                      <div className="col-span-4">
                        <Label className="text-xs">Branch Name</Label>
                        <Input value={b.name} placeholder="e.g. Kolhapur Campus" onChange={(e) => updateBranch(idx, 'name', e.target.value)} data-testid={'wizard-branch-name-' + idx} />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Code</Label>
                        <Input value={b.code} placeholder="UNIQUE" onChange={(e) => updateBranch(idx, 'code', e.target.value.toUpperCase())} data-testid={'wizard-branch-code-' + idx} />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">City</Label>
                        <Input value={b.city} onChange={(e) => updateBranch(idx, 'city', e.target.value)} data-testid={'wizard-branch-city-' + idx} />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <Button size="sm" variant="ghost" onClick={() => removeBranchRow(idx)} data-testid={'wizard-branch-remove-' + idx}>Remove</Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addBranchRow} data-testid="wizard-add-branch-row">
                    <Plus className="h-3 w-3 mr-1" /> Add another branch
                  </Button>
                </div>
              ) : null}

              {wizardStep === 3 ? (
                <div className="space-y-3">
                  <div className="rounded-md bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-900 p-3 text-xs text-blue-900 dark:text-blue-100">
                    This person will become the <strong>Institute Admin</strong> and can manage all employees, attendance, leaves, payroll, and recruitment <strong>only inside this institute and its branches</strong>. You can change permissions later from the Users page.
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Admin Name *</Label>
                      <Input value={wizardForm.admin.name} onChange={(e) => setWizardForm((f) => ({ ...f, admin: { ...f.admin, name: e.target.value } }))} data-testid="wizard-admin-name" />
                    </div>
                    <div>
                      <Label>Admin Email *</Label>
                      <Input type="email" value={wizardForm.admin.email} onChange={(e) => setWizardForm((f) => ({ ...f, admin: { ...f.admin, email: e.target.value } }))} data-testid="wizard-admin-email" />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input value={wizardForm.admin.phone} onChange={(e) => setWizardForm((f) => ({ ...f, admin: { ...f.admin, phone: e.target.value } }))} data-testid="wizard-admin-phone" />
                    </div>
                    <div>
                      <Label>Temp Password <span className="text-xs text-slate-500">(leave blank to auto-generate)</span></Label>
                      <Input value={wizardForm.admin.password} onChange={(e) => setWizardForm((f) => ({ ...f, admin: { ...f.admin, password: e.target.value } }))} data-testid="wizard-admin-password" />
                    </div>
                  </div>
                </div>
              ) : null}

              <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={() => setWizardOpen(false)}>Cancel</Button>
                {wizardStep > 1 ? (
                  <Button variant="outline" onClick={() => setWizardStep((s) => s - 1)} data-testid="wizard-back-btn">Back</Button>
                ) : null}
                {wizardStep < 3 ? (
                  <Button onClick={() => {
                    if (wizardStep === 1) {
                      const err = validateStep1();
                      if (err) { toast.error(err); return; }
                    }
                    setWizardStep((s) => s + 1);
                  }} data-testid="wizard-next-btn">Next</Button>
                ) : (
                  <Button onClick={submitWizard} disabled={submitting} data-testid="wizard-submit-btn">
                    {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Create Institute & Admin
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Institute Admin Password */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setResetResult(null); } }}>
        <DialogContent className="max-w-md" data-testid="branches-reset-admin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-amber-600" /> Reset Institute Admin Password</DialogTitle>
            <DialogDescription>
              Generate a fresh temporary password for the Institute Admin of <strong>{resetTarget?.name}</strong>. The admin's old password will stop working immediately.
            </DialogDescription>
          </DialogHeader>
          {!resetResult ? (
            <div className="text-sm text-slate-600 dark:text-slate-300">
              We'll create a fresh password and show it on the next screen. Use this when:
              <ul className="list-disc pl-5 mt-1 space-y-0.5">
                <li>The admin forgot their password</li>
                <li>The temp password from the wizard was lost</li>
                <li>You're rotating credentials</li>
              </ul>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-900 p-3 text-sm space-y-1" data-testid="reset-admin-result">
              <div className="grid grid-cols-3 gap-2">
                <div className="text-slate-500">Email</div>
                <div className="col-span-2 font-mono break-all">{resetResult.email}</div>
                <div className="text-slate-500">Password</div>
                <div className="col-span-2 font-mono select-all font-bold" data-testid="reset-admin-password">{resetResult.temp_password}</div>
              </div>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => {
                navigator.clipboard?.writeText(`Email: ${resetResult.email}\nPassword: ${resetResult.temp_password}\nLogin: ${window.location.origin}/login`);
                toast.success('Copied to clipboard');
              }} data-testid="reset-admin-copy">Copy credentials</Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setResetTarget(null); setResetResult(null); }}>Close</Button>
            {!resetResult ? (
              <Button onClick={doResetAdmin} disabled={submitting} data-testid="reset-admin-confirm">
                {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1" />}
                Generate New Password
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={seedOpen} onOpenChange={setSeedOpen}>
        <DialogContent data-testid="branches-seed-dialog">
          <DialogHeader>
            <DialogTitle>Seed Trust / Holding Root</DialogTitle>
            <DialogDescription>
              Creates a single top-level Trust entity and automatically re-parents every existing root entity under it.
              Safe to run multiple times - reruns are no-ops if the code already exists.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input value={seedForm.name} onChange={(e) => setSeedForm((f) => ({ ...f, name: e.target.value }))} data-testid="seed-form-name" />
            </div>
            <div>
              <Label>Code</Label>
              <Input value={seedForm.code} onChange={(e) => setSeedForm((f) => ({ ...f, code: e.target.value }))} data-testid="seed-form-code" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={seedForm.city} onChange={(e) => setSeedForm((f) => ({ ...f, city: e.target.value }))} data-testid="seed-form-city" />
            </div>
            <div className="col-span-2">
              <Label>State</Label>
              <Input value={seedForm.state} onChange={(e) => setSeedForm((f) => ({ ...f, state: e.target.value }))} data-testid="seed-form-state" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSeedOpen(false)}>Cancel</Button>
            <Button onClick={seedTrustRoot} disabled={submitting} data-testid="seed-form-submit">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Seed Trust
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
